import React, { useEffect, useState } from "react";
import { Radar, PlayCircle, Sparkles, MapPin } from "lucide-react";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import Skeleton from "@/components/jea/Skeleton";
import PillButton from "@/components/jea/PillButton";
import { demoClient, useDemo } from "@/pages/demo/DemoShell";
import { formatMoney } from "@/lib/pricing";
import { cn } from "@/lib/utils";

const COURT_LEVELS = [
  { key: "small_claims", label: "Small Claims",  guide: "Fast-track collection paths. Wage garnishment via employer statement." },
  { key: "county",       label: "County / State",guide: "Full civil-court remedies: bank levy, wage garnishment, property lien." },
  { key: "federal",      label: "Federal",       guide: "Enhanced disclosure powers. Nationwide asset search + PACER lookups." },
];

const DemoDiscoveryPage = () => {
  const demo = useDemo();
  const [brokers, setBrokers] = useState([]);
  const [debtors, setDebtors] = useState([]);
  const [debtorId, setDebtorId] = useState(null);
  const [court, setCourt] = useState("county");
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [b, d] = await Promise.all([
          demoClient.get("/demo/brokers"),
          demoClient.get("/demo/debtors"),
        ]);
        setBrokers(b.data.brokers || []);
        setDebtors(d.data.debtors || []);
        if (d.data.debtors?.length) setDebtorId(d.data.debtors[0].id);
        // Preselect the top 3 recommended brokers
        setSelected(new Set((b.data.brokers || []).slice(0, 3).map((x) => x.slug)));
      } finally { setLoading(false); }
    })();
  }, []);

  const guide = COURT_LEVELS.find((c) => c.key === court);
  const nudge = () => demo?.openSignUp("running discovery searches");
  const selectedBrokers = brokers.filter((b) => selected.has(b.slug));
  const subtotal = selectedBrokers.reduce((s, b) => s + (b.price_pro_cents || b.price_standard_cents || 0), 0);
  const discount = selectedBrokers.length >= 5 ? Math.floor(subtotal * 0.15) : 0;
  const total = subtotal - discount;

  const toggle = (slug) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  };

  return (
    <div className="space-y-6" data-testid="demo-discovery-page">
      <div>
        <div className="jea-caption">Court-Aware Asset Discovery</div>
        <h1 className="text-[28px] font-bold text-white">Discovery</h1>
        <p className="text-text-secondary text-[14px] mt-1">
          Different courts have different remedies. We surface the right rules — and the right brokers — for every case.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6" data-tour="discovery-panel">
        <div className="space-y-6">
          <JeaCard>
            <div className="jea-caption mb-3">Court Level</div>
            <div className="flex flex-wrap gap-2 mb-4">
              {COURT_LEVELS.map((c) => (
                <PillButton
                  key={c.key}
                  active={c.key === court}
                  onClick={() => setCourt(c.key)}
                  data-testid={`demo-court-${c.key}`}
                >
                  {c.label}
                </PillButton>
              ))}
            </div>
            <div className="bg-bg-tertiary rounded-[8px] p-4 text-text-secondary text-[13px]">
              <MapPin size={12} className="inline mr-2 text-accent-pink" />
              {guide?.guide}
            </div>
          </JeaCard>

          <JeaCard>
            <div className="jea-caption mb-3">Debtor</div>
            {loading ? <Skeleton className="h-16" /> : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {debtors.slice(0, 4).map((d) => {
                  const active = d.id === debtorId;
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => setDebtorId(d.id)}
                      className={cn(
                        "text-left rounded-[8px] border p-3 transition-all",
                        active
                          ? "border-accent-pink bg-accent-pink/10"
                          : "border-border-default hover:border-accent-pink/40"
                      )}
                      data-testid={`demo-discovery-debtor-${d.id}`}
                    >
                      <div className="text-white text-[13px] font-semibold truncate">{d.full_name}</div>
                      <div className="text-text-muted text-[11px]">{d.judgment?.state} · Case {d.judgment?.case_number}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </JeaCard>

          <JeaCard>
            <div className="flex items-center justify-between mb-3">
              <div className="jea-caption">Broker Sources ({selectedBrokers.length} selected)</div>
              <div className="text-text-secondary text-[11px]">Discount applied at 5+ brokers</div>
            </div>
            {loading ? <Skeleton className="h-40" /> : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {brokers.map((b) => {
                  const active = selected.has(b.slug);
                  return (
                    <button
                      key={b.slug}
                      type="button"
                      onClick={() => toggle(b.slug)}
                      className={cn(
                        "text-left rounded-[8px] border p-3 transition-all",
                        active
                          ? "border-accent-pink bg-accent-pink/10"
                          : "border-border-default hover:border-accent-pink/40"
                      )}
                      data-testid={`demo-broker-${b.slug}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-white text-[13px] font-semibold truncate">{b.name}</span>
                        <span className="jea-mono text-accent-pink text-[12px] font-bold">
                          {formatMoney(b.price_pro_cents || b.price_standard_cents || 0)}
                        </span>
                      </div>
                      <div className="text-text-muted text-[11px] truncate">{b.category}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </JeaCard>
        </div>

        <div className="space-y-4">
          <JeaCard data-testid="demo-discovery-summary">
            <div className="jea-caption">Search Cost</div>
            <div className="mt-2 space-y-1 text-[13px]">
              <div className="flex justify-between text-text-secondary">
                <span>Subtotal</span>
                <span className="jea-mono">{formatMoney(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-success">
                  <span>Bundle discount</span>
                  <span className="jea-mono">-{formatMoney(discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-white font-bold pt-2 border-t border-border-default mt-2">
                <span>Total</span>
                <span className="jea-mono text-accent-pink text-[18px]">{formatMoney(total)}</span>
              </div>
            </div>
            <JeaButton
              fullWidth
              className="mt-4 gap-2"
              onClick={nudge}
              disabled={selectedBrokers.length === 0}
              data-testid="demo-discovery-cta"
            >
              <PlayCircle size={16} /> Run Selected Searches
            </JeaButton>
          </JeaCard>

          <JeaCard>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={14} className="text-accent-pink" />
              <div className="jea-caption">Why Bundle?</div>
            </div>
            <p className="text-text-secondary text-[12px]">
              Every additional broker cross-validates the last. Confidence goes up. Missed
              assets go down. 5+ brokers unlocks 15% bundle discount.
            </p>
          </JeaCard>
        </div>
      </div>
    </div>
  );
};

export default DemoDiscoveryPage;
